import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { ArrowLeft, Save } from "lucide-react";

const empty = () => ({
    kind: "Mobile Postpaid",
    operator: "",
    account_or_number: "",
    account_label: "",
    holder_name: "",
    family_member: "",
    business_personal: "Personal",
    plan_name: "",
    plan_type: "",
    monthly_rent: 0,
    gst_pct: 18,
    data_limit_gb: "",
    voice_minutes: "",
    sms_limit: "",
    ott_bundle: "",
    vas_addons_csv: "",
    billing_start_day: "",
    billing_end_day: "",
    invoice_gen_day: "",
    payment_due_day: "",
    frequency: "Monthly",
    auto_debit: false,
    auto_debit_bank_account_id: "",
    default_payment_card_id: "",
    upi_id: "",
    portal_link: "",
    registered_email: "",
    registered_mobile: "",
    invoice_email_from: "",
    invoice_email_subject_pattern: "",
    provider_gstin: "",
    my_gstin: "",
    input_tax_credit: false,
    circle: "",
    address: "",
    portable_to: "",
    activation_date: "",
    plan_expiry_date: "",
    renewal_month: "",
    primary_device_model: "",
    imei: "",
    alternate_contact: "",
    meter_number: "",
    consumer_number: "",
    sanctioned_load: "",
    status: "Active",
    color_tag: "#0369a1",
    remarks: "",
});

const Row = ({ children }) => (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-3 py-2">{children}</div>
);
const Field = ({ label, children, hint }) => (
    <div>
        <Label className="eyebrow">{label}</Label>
        {children}
        {hint && <div className="text-[10.5px] text-stone-400 mt-0.5">{hint}</div>}
    </div>
);
const Section = ({ title, subtitle, children }) => (
    <div className="form-section mb-4">
        <div className="mb-2">
            <div className="font-display text-lg text-stone-900">{title}</div>
            {subtitle && <div className="text-[11px] text-stone-500">{subtitle}</div>}
        </div>
        {children}
    </div>
);

export default function BillForm() {
    const { id } = useParams();
    const editing = Boolean(id);
    const nav = useNavigate();
    const [f, setF] = useState(empty());
    const [meta, setMeta] = useState({ kinds: [], statuses: [], frequencies: [] });
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        api.get("/bills/meta").then((r) => setMeta(r.data));
        if (editing) {
            api.get(`/bills/${id}`).then((r) => {
                const d = r.data;
                setF({
                    ...empty(), ...d,
                    vas_addons_csv: (d.vas_addons || []).join(", "),
                });
            });
        }
    }, [id, editing]);

    const set = (k) => (v) => setF((s) => ({ ...s, [k]: v }));
    const setInput = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            const payload = { ...f };
            // number coercions
            ["monthly_rent", "gst_pct", "data_limit_gb", "voice_minutes", "sms_limit",
             "billing_start_day", "billing_end_day", "invoice_gen_day",
             "payment_due_day", "renewal_month"].forEach((k) => {
                if (payload[k] === "" || payload[k] === null || payload[k] === undefined) {
                    payload[k] = null;
                } else {
                    payload[k] = Number(payload[k]);
                }
            });
            payload.vas_addons = f.vas_addons_csv.split(",").map((s) => s.trim()).filter(Boolean);
            delete payload.vas_addons_csv;
            delete payload.id;
            delete payload.masked_account;
            delete payload.archived;

            if (editing) {
                await api.patch(`/bills/${id}`, payload);
                toast.success("Connection updated");
                nav(`/bills/${id}`);
            } else {
                const r = await api.post("/bills", payload);
                toast.success("Connection added");
                nav(`/bills/${r.data.id}`);
            }
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Failed to save");
        } finally { setBusy(false); }
    };

    const isMobile = f.kind?.startsWith("Mobile") || f.kind === "Landline";
    const isPrepaid = f.kind === "Mobile Prepaid";
    const isUtility = ["Electricity", "Water", "Gas Cylinder", "Piped Gas", "Society Maintenance"].includes(f.kind);

    return (
        <div data-testid="bill-form-page" className="max-w-4xl">
            <button onClick={() => nav("/bills")} className="text-sm text-stone-500 hover:text-stone-900 inline-flex items-center gap-1 mb-4">
                <ArrowLeft size={14} /> Back to bills
            </button>
            <div className="mb-6">
                <div className="eyebrow">{editing ? "Edit connection" : "New connection"}</div>
                <h1 className="font-display text-4xl text-stone-900 leading-none mt-2">
                    {editing ? f.operator + " · " + (f.account_label || f.account_or_number) : "Add a recurring bill"}
                </h1>
            </div>

            <form onSubmit={submit}>
                <Section title="Identity" subtitle="What kind of connection is this?">
                    <Row>
                        <Field label="Kind">
                            <Select value={f.kind} onValueChange={set("kind")}>
                                <SelectTrigger data-testid="bill-form-kind"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    {meta.kinds.map((k) => <SelectItem key={k} value={k}>{k}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </Field>
                        <Field label="Operator / Provider">
                            <Input value={f.operator} onChange={setInput("operator")} required placeholder="Airtel / Jio / Tata Power…" data-testid="bill-form-operator" />
                        </Field>
                        <Field label={isMobile ? "Phone number" : "Account / Consumer number"}>
                            <Input value={f.account_or_number} onChange={setInput("account_or_number")} required data-testid="bill-form-account" />
                        </Field>
                    </Row>
                    <Row>
                        <Field label="Nickname" hint="e.g. Personal main / Office broadband / Guest WiFi">
                            <Input value={f.account_label} onChange={setInput("account_label")} data-testid="bill-form-label" />
                        </Field>
                        <Field label="Holder">
                            <Input value={f.holder_name} onChange={setInput("holder_name")} />
                        </Field>
                        <Field label="Family member" hint="Who uses this connection">
                            <Input value={f.family_member} onChange={setInput("family_member")} />
                        </Field>
                    </Row>
                    <Row>
                        <Field label="Business / Personal">
                            <Select value={f.business_personal} onValueChange={set("business_personal")}>
                                <SelectTrigger><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="Personal">Personal</SelectItem>
                                    <SelectItem value="Business">Business</SelectItem>
                                </SelectContent>
                            </Select>
                        </Field>
                        <Field label="Status">
                            <Select value={f.status} onValueChange={set("status")}>
                                <SelectTrigger><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    {(meta.statuses || []).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </Field>
                        <Field label="Color tag">
                            <Input type="color" value={f.color_tag} onChange={setInput("color_tag")} className="h-9 w-24" />
                        </Field>
                    </Row>
                </Section>

                <Section title="Plan" subtitle="Rent, tariff, data & voice allowances">
                    <Row>
                        <Field label="Plan name">
                            <Input value={f.plan_name} onChange={setInput("plan_name")} placeholder="Infinity 549 / FTTH 300 Mbps…" />
                        </Field>
                        <Field label="Monthly rent (₹)">
                            <Input type="number" step="0.01" value={f.monthly_rent} onChange={setInput("monthly_rent")} data-testid="bill-form-rent" />
                        </Field>
                        <Field label="GST %">
                            <Input type="number" step="0.01" value={f.gst_pct} onChange={setInput("gst_pct")} />
                        </Field>
                    </Row>
                    {(isMobile || f.kind === "Broadband") && (
                        <Row>
                            <Field label="Data limit (GB)">
                                <Input type="number" value={f.data_limit_gb} onChange={setInput("data_limit_gb")} />
                            </Field>
                            <Field label="Voice minutes">
                                <Input type="number" value={f.voice_minutes} onChange={setInput("voice_minutes")} />
                            </Field>
                            <Field label="SMS limit">
                                <Input type="number" value={f.sms_limit} onChange={setInput("sms_limit")} />
                            </Field>
                        </Row>
                    )}
                    <Row>
                        <Field label="OTT bundle" hint="e.g. Netflix + Prime + Disney+">
                            <Input value={f.ott_bundle} onChange={setInput("ott_bundle")} />
                        </Field>
                        <Field label="VAS add-ons (comma-separated)">
                            <Input value={f.vas_addons_csv} onChange={setInput("vas_addons_csv")} placeholder="Wynk, Roaming, ISD Pack" />
                        </Field>
                        <Field label="Frequency">
                            <Select value={f.frequency} onValueChange={set("frequency")}>
                                <SelectTrigger><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    {(meta.frequencies || []).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </Field>
                    </Row>
                </Section>

                <Section title="Billing cycle" subtitle="When does it generate and when do you pay?">
                    <Row>
                        <Field label="Billing period start · day"><Input type="number" min="1" max="31" value={f.billing_start_day} onChange={setInput("billing_start_day")} /></Field>
                        <Field label="Billing period end · day"><Input type="number" min="1" max="31" value={f.billing_end_day} onChange={setInput("billing_end_day")} /></Field>
                        <Field label="Invoice gen day"><Input type="number" min="1" max="31" value={f.invoice_gen_day} onChange={setInput("invoice_gen_day")} /></Field>
                    </Row>
                    <Row>
                        <Field label="Payment due day"><Input type="number" min="1" max="31" value={f.payment_due_day} onChange={setInput("payment_due_day")} /></Field>
                        <Field label="Activation date"><Input type="date" value={f.activation_date} onChange={setInput("activation_date")} /></Field>
                        {isPrepaid ? (
                            <Field label="Plan expires on" hint="VAULT alerts you before this date"><Input type="date" value={f.plan_expiry_date} onChange={setInput("plan_expiry_date")} /></Field>
                        ) : (
                            <Field label="Annual renewal month"><Input type="number" min="1" max="12" value={f.renewal_month} onChange={setInput("renewal_month")} /></Field>
                        )}
                    </Row>
                </Section>

                <Section title="Payment defaults" subtitle="Default source used to clear these bills">
                    <Row>
                        <div className="flex items-center gap-3 border border-stone-200 rounded-md px-3 py-2.5">
                            <div className="flex-1">
                                <div className="text-sm text-stone-900">Auto-debit enabled</div>
                                <div className="text-[11px] text-stone-500">Ignore for reminders if on</div>
                            </div>
                            <Switch checked={f.auto_debit} onCheckedChange={set("auto_debit")} />
                        </div>
                        <Field label="UPI ID"><Input value={f.upi_id} onChange={setInput("upi_id")} placeholder="9876543210@upi" /></Field>
                        <Field label="Portal link"><Input value={f.portal_link} onChange={setInput("portal_link")} placeholder="https://…" /></Field>
                    </Row>
                </Section>

                <Section title="Registration & auto-fetch" subtitle="Details we use to sync invoices from your inbox">
                    <Row>
                        <Field label="Registered email"><Input type="email" value={f.registered_email} onChange={setInput("registered_email")} /></Field>
                        <Field label="Registered mobile"><Input value={f.registered_mobile} onChange={setInput("registered_mobile")} /></Field>
                        <Field label="Alternate contact"><Input value={f.alternate_contact} onChange={setInput("alternate_contact")} /></Field>
                    </Row>
                    <Row>
                        <Field label="Invoice email from" hint="e.g. donotreply@airtel.in"><Input value={f.invoice_email_from} onChange={setInput("invoice_email_from")} /></Field>
                        <Field label="Invoice subject pattern" hint="e.g. Your Airtel bill for"><Input value={f.invoice_email_subject_pattern} onChange={setInput("invoice_email_subject_pattern")} /></Field>
                        <Field label="Circle / location"><Input value={f.circle} onChange={setInput("circle")} placeholder="Delhi / Karnataka / …" /></Field>
                    </Row>
                </Section>

                {isMobile && (
                    <Section title="Device" subtitle="Primary phone using this SIM">
                        <Row>
                            <Field label="Primary device"><Input value={f.primary_device_model} onChange={setInput("primary_device_model")} placeholder="iPhone 15 / OnePlus 12…" /></Field>
                            <Field label="IMEI" hint="15-digit; kept private"><Input value={f.imei} onChange={setInput("imei")} /></Field>
                            <Field label="Portable to (planned MNP)"><Input value={f.portable_to} onChange={setInput("portable_to")} placeholder="Jio / Airtel / Vi" /></Field>
                        </Row>
                    </Section>
                )}

                {isUtility && (
                    <Section title="Meter & premises">
                        <Row>
                            <Field label="Meter number"><Input value={f.meter_number} onChange={setInput("meter_number")} /></Field>
                            <Field label="Consumer number"><Input value={f.consumer_number} onChange={setInput("consumer_number")} /></Field>
                            <Field label="Sanctioned load"><Input value={f.sanctioned_load} onChange={setInput("sanctioned_load")} placeholder="e.g. 5 kW" /></Field>
                        </Row>
                        <Row>
                            <Field label="Service address"><Textarea rows={2} value={f.address} onChange={setInput("address")} /></Field>
                        </Row>
                    </Section>
                )}

                {f.business_personal === "Business" && (
                    <Section title="Business / GST">
                        <Row>
                            <Field label="Provider GSTIN"><Input value={f.provider_gstin} onChange={setInput("provider_gstin")} /></Field>
                            <Field label="My GSTIN"><Input value={f.my_gstin} onChange={setInput("my_gstin")} /></Field>
                            <div className="flex items-center gap-3 border border-stone-200 rounded-md px-3 py-2.5">
                                <div className="flex-1">
                                    <div className="text-sm text-stone-900">Input tax credit eligible</div>
                                </div>
                                <Switch checked={f.input_tax_credit} onCheckedChange={set("input_tax_credit")} />
                            </div>
                        </Row>
                    </Section>
                )}

                <Section title="Notes">
                    <div>
                        <Label className="eyebrow">Remarks</Label>
                        <Textarea rows={2} value={f.remarks} onChange={setInput("remarks")} />
                    </div>
                </Section>

                <div className="flex items-center justify-end gap-2 sticky bottom-4 bg-stone-50/80 backdrop-blur border border-stone-200 rounded-lg p-3 mt-4">
                    <Button type="button" variant="outline" onClick={() => nav("/bills")}>Cancel</Button>
                    <Button type="submit" disabled={busy} className="bg-stone-900 hover:bg-stone-800 text-white" data-testid="bill-form-save">
                        <Save size={14} className="mr-1.5" /> {busy ? "Saving…" : editing ? "Save changes" : "Add connection"}
                    </Button>
                </div>
            </form>
        </div>
    );
}
